<script lang="ts" setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { RouteLocationRaw, useRoute, RouterLink } from 'vue-router'

import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { useConfirm } from '@/hooks/use-confirm'
import { useAuthStore } from '@/stores/auth-store'
import { MyDateRangePickerRef, PaginatedResponse, uuid } from '@/types/general'
import { MyButtonScheme } from '@/types/layout/my-button'
import { GetDataParameters } from '@/types/table'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { MinimalPackagingOrder, PackagingOrderStatus, PackagingOrderType } from '@/types/packaging'
import { DropdownStringOption } from '@/types/inputs'
import { datetime } from '@/utils/dates'

import ActionRowItem from '@/components/table/ActionRowItem.vue'
import OrderStatus from '@/components/packaging/PackagingOrderStatus.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import MyFilterButton from '@/components/table/MyFilterButton.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import MyFilterSelect from '@/components/table/MyFilterSelect.vue'
import MyFilterDivider from '@/components/table/MyFilterDivider.vue'
import ManagePackagingOrderModal from '@/components/packaging/ManagePackagingOrderModal.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'
import MyButton from '@/components/my-components/MyButton.vue'

interface FetchPackagingOrdersParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        type?: PackagingOrderType
        status?: string // PackagingOrderStatus as string
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const { t } = useI18n()
const authStore = useAuthStore()
const confirm = useConfirm()
const route = useRoute()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalPackagingOrder,
    FetchPackagingOrdersParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<MinimalPackagingOrder>>(
            window.route('packaging.company.packaging-orders.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            type: undefined,
            status: undefined,
            'start-date': dayjs().subtract(1, 'month').startOf('day').toDate().toISOString(),
            'end-date': dayjs().endOf('day').toDate().toISOString(),
        },
    },
)

const managePackagingOrderModalOpen = ref(false)
const selectedPackagingOrder = ref<MinimalPackagingOrder>()
const dateRangePickerRef = ref<MyDateRangePickerRef>()

const orderTypes = computed<DropdownStringOption[]>(() => [
    { value: PackagingOrderType.Pickup.toString(), label: t('pickup') },
    { value: PackagingOrderType.Delivery.toString(), label: t('delivery') },
])

function showPackagingOrder(id: uuid): RouteLocationRaw {
    return {
        name: 'packaging.packaging-orders.show',
        params: { packagingOrderId: id },
        query: route.query,
    }
}

function updatePackagingOrder(packagingOrder: MinimalPackagingOrder) {
    selectedPackagingOrder.value = packagingOrder
    managePackagingOrderModalOpen.value = true
}

async function deletePackagingOrder(id: uuid) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('packagingOrder') }),
            t('deleteEntityDescription', { entity: t('packagingOrder') }),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
                confirmButtonScheme: MyButtonScheme.Warning,
            },
        )
    } catch {
        return
    }

    await axios.delete(
        window.route('packaging.company.packaging-orders.destroy', {
            company: authStore.companyId,
            packaging_order: id,
        }),
    )
    await refetch()
}

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}

useMittListener('fetchPackagingOrders', () => refetch())
</script>

<template>
    <LoaderWrapper :visible="loading" />

    <MyTable
        table-id="pm-packaging-orders"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('packagingOrders')"
    >
        <template #filters>
            <MyDateRangePicker
                ref="dateRangePickerRef"
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />

            <MyFilterDivider />

            <MyFilterButton
                filter-name="status"
                :value="null"
                default
                @selected="setFilter"
                v-text="t('all')"
            />

            <MyFilterButton
                filter-name="status"
                :value="PackagingOrderStatus.Created.toString()"
                @selected="setFilter"
                v-text="t('created')"
            />

            <MyFilterButton
                filter-name="status"
                :value="PackagingOrderStatus.Accepted.toString()"
                @selected="setFilter"
                v-text="t('accepted')"
            />
            <MyFilterButton
                filter-name="status"
                :value="PackagingOrderStatus.Finished.toString()"
                @selected="setFilter"
                v-text="t('finished')"
            />

            <MyFilterButton
                filter-name="status"
                :value="PackagingOrderStatus.Canceled.toString()"
                @selected="setFilter"
                v-text="t('canceled')"
            />

            <MyFilterDivider />

            <MyFilterSelect
                :label="t('type')"
                :options="orderTypes"
                :dropdown-width="240"
                filter-name="type"
                translation-key="type"
                @change="setFilter"
            />
        </template>

        <template #actionRow="{ row }">
            <slot name="customActionRow" :row="row">
                <RouterLink :to="showPackagingOrder(row.id)">
                    <ActionRowItem>
                        <mdi:eye />
                    </ActionRowItem>
                </RouterLink>
            </slot>
        </template>

        <template #actionMenu="{ row }">
            <ActionMenuItem
                v-if="row.status === PackagingOrderStatus.Created"
                @click="updatePackagingOrder(row)"
            >
                <mdi:pencil class="mr-2" />
                {{ t('edit') }}
            </ActionMenuItem>

            <ActionMenuItem
                class="flex cursor-pointer items-center rounded-lg py-2 px-3 text-sm hover:bg-primary-500 hover:text-primary-50 dark:hover:bg-dark-300"
                @click="deletePackagingOrder(row.id)"
            >
                <mdi:trash-can class="mr-2" />
                {{ t('delete') }}
            </ActionMenuItem>
        </template>

        <MyTableColumn :name="t('supplier')" property="supplierCompany.name" />
        <MyTableColumn :name="t('supplierLocation')" property="supplierLocation.name" />
        <MyTableColumn :name="t('customer')" property="customerCompany.name" />
        <MyTableColumn :name="t('customerLocation')" property="customerLocation.name" />
        <MyTableColumn :name="t('type')" property="type" />
        <template #type="{ row }">
            <span
                v-if="row"
                v-text="t(row.type === PackagingOrderType.Pickup ? 'pickup' : 'delivery')"
            />
        </template>
        <MyTableColumn :name="t('status')" property="status" />
        <template #status="{ row }">
            <OrderStatus v-if="row" :packaging-order="row" />
        </template>
        <MyTableColumn :name="t('orderNumber')" property="orderNumber" />
        <MyTableColumn :name="t('createdAt')" property="createdAt" />
        <template #createdAt="{ row }">
            <span v-text="datetime(row.createdAt)" />
        </template>
        <MyTableColumn :name="t('updatedAt')" property="updatedAt" />
        <template #updatedAt="{ row }">
            <span v-text="datetime(row.updatedAt)" />
        </template>
        <MyTableColumn :name="t('deliveryPickupDate')" property="plannedAt" />
        <template #plannedAt="{ row }">
            <span v-text="datetime(row.plannedAt)" />
        </template>

        <template #noDataButton>
            <MyButton
                v-if="params.filter['start-date']"
                plain
                scheme="light"
                size="small"
                @click="dateRangePickerRef?.clearDates"
            >
                <mdi:magnify class="mr-2 h-4 w-4" />
                {{ t('searchAllTime') }}
            </MyButton>
        </template>
    </MyTable>

    <ManagePackagingOrderModal
        v-model="managePackagingOrderModalOpen"
        :packaging-order-id="selectedPackagingOrder?.id"
        @close="managePackagingOrderModalOpen = false"
    />
</template>
